import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Users } from 'lucide-react';
import { useMemo } from 'react';

import type { UserModel } from '@/domain/models/user.js';
import { DataTable, EmptyState } from '@/presentation/components/index.js';
import type { DataTableColumn } from '@/presentation/components/index.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { UserActionsMenu } from '@/presentation/pages/users/components/user-actions-menu.js';
import { UserNameCell } from '@/presentation/pages/users/components/user-name-cell.js';
import { getUserProfileChips } from '@/presentation/pages/users/utils/get-user-profile-chips.js';
import type { UserConfirmAction } from '@/presentation/pages/users/utils/resolve-user-confirm-dialog.js';
import { resolveUserStatusChipColors } from '@/presentation/pages/users/utils/resolve-user-status-chip-colors.js';
import { BRAND_NAVY } from '@/shared/constants/brand.js';
import { formatDate } from '@/shared/utils/format-date.js';

interface Props {
    users: UserModel[];
    total: number;
    page: number;
    limit: number;
    canManage: boolean;
    meId: string | undefined;
    onEdit: (userId: string) => void;
    onPageChange: (nextPage: number) => void;
    onLimitChange: (nextLimit: number) => void;
    onConfirmAction: (action: UserConfirmAction) => void;
}

export function UserListTable({ users, total, page, limit, canManage, meId, onEdit, onPageChange, onLimitChange, onConfirmAction }: Props) {
    const { translate } = useApp();
    const isMe = (id: string) => id === meId;

    const columns: DataTableColumn<UserModel>[] = useMemo(() => {
        const baseColumns: DataTableColumn<UserModel>[] = [
            {
                key: 'name',
                header: translate('users.name'),
                sortValue: (row) => row.name,
                render: (row) => <UserNameCell name={row.name} avatarUrl={row.avatarUrl} />
            },
            {
                key: 'email',
                header: translate('users.email'),
                sortValue: (row) => row.email,
                render: (row) => (
                    <Typography variant="body2" color="text.secondary">
                        {row.email}
                    </Typography>
                )
            },
            {
                key: 'profile',
                header: translate('users.profile'),
                sortValue: (row) => getUserProfileChips(row).join(', '),
                render: (row) => (
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        {getUserProfileChips(row).map((p) => (
                            <Chip key={p} label={translate(`profiles.${p}`)} size="small" sx={{ bgcolor: `${BRAND_NAVY}1A`, color: BRAND_NAVY, fontWeight: 500, fontSize: 12 }} />
                        ))}
                    </Stack>
                )
            },
            {
                key: 'status',
                header: translate('users.status'),
                sortValue: (row) => row.status,
                render: (row) => (
                    <Chip
                        label={translate(row.status === 'ATIVO' ? 'users.active' : 'users.blocked')}
                        size="small"
                        sx={{ ...resolveUserStatusChipColors(row.status), fontWeight: 500, fontSize: 12 }}
                    />
                )
            },
            {
                key: 'lastLoginAt',
                header: translate('users.lastLogin'),
                sortValue: (row) => row.lastLoginAt ?? '',
                render: (row) => (
                    <Typography variant="body2" color="text.secondary">
                        {formatDate(row.lastLoginAt)}
                    </Typography>
                )
            }
        ];

        if (!canManage) {
            return baseColumns;
        }

        return [
            ...baseColumns,
            {
                key: 'actions',
                header: '',
                sortable: false,
                hideable: false,
                width: 48,
                render: (row) => (
                    <UserActionsMenu
                        status={row.status}
                        isSelf={isMe(row.id)}
                        labels={{
                            edit: translate('common.edit'),
                            block: translate('users.blockUser'),
                            unblock: translate('users.unblockUser'),
                            resetPassword: translate('users.resetPassword')
                        }}
                        onEdit={() => onEdit(row.id)}
                        onBlock={() => onConfirmAction({ type: 'block', user: row })}
                        onUnblock={() => onConfirmAction({ type: 'unblock', user: row })}
                        onReset={() => onConfirmAction({ type: 'reset', user: row })}
                    />
                )
            }
        ];
    }, [canManage, translate, meId, onEdit, onConfirmAction]);

    if (users.length === 0) {
        return <EmptyState icon={<Users size={24} />} title={translate('users.noUsersFound')} message={translate('users.adjustFiltersOrCreate')} />;
    }

    return (
        <DataTable
            columns={columns}
            rows={users}
            getRowKey={(row) => row.id}
            onRowClick={(row) => onEdit(row.id)}
            page={page - 1}
            rowsPerPage={limit}
            total={total}
            onPageChange={(nextPage) => onPageChange(nextPage + 1)}
            onRowsPerPageChange={onLimitChange}
        />
    );
}
